/**
 * Partner Report Field Mappings API
 * Handles API calls for field mapping management
 */

import axiosInstance from '../axiosInstance';
import type {
  FieldMapping,
  CreateFieldMappingRequest,
  ApiResponse,
  ApiListResponse,
} from '@/types/partnerReports';

const BASE_URL = '/api/partner-field-mappings';

/**
 * Get all field mappings for a partner
 */
export const getPartnerFieldMappings = async (
  partnerId: string
): Promise<FieldMapping[]> => {
  const response = await axiosInstance.get<ApiListResponse<FieldMapping>>(
    `${BASE_URL}/partner/${partnerId}`
  );
  return response.data.data;
};

/**
 * Get active field mapping for a partner and file type
 */
export const getActiveFieldMapping = async (
  partnerId: string,
  fileType: string
): Promise<FieldMapping | null> => {
  try {
    const response = await axiosInstance.get<ApiResponse<FieldMapping>>(
      `${BASE_URL}/partner/${partnerId}/active/${fileType}`
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Get a specific field mapping by ID
 */
export const getFieldMapping = async (id: string): Promise<FieldMapping> => {
  const response = await axiosInstance.get<ApiResponse<FieldMapping>>(
    `${BASE_URL}/${id}`
  );
  return response.data.data;
};

/**
 * Create a new field mapping
 */
export const createFieldMapping = async (
  data: CreateFieldMappingRequest
): Promise<FieldMapping> => {
  const response = await axiosInstance.post<ApiResponse<FieldMapping>>(
    BASE_URL,
    data
  );
  return response.data.data;
};

/**
 * Update an existing field mapping
 */
export const updateFieldMapping = async (
  id: string,
  data: Partial<CreateFieldMappingRequest>
): Promise<FieldMapping> => {
  const response = await axiosInstance.put<ApiResponse<FieldMapping>>(
    `${BASE_URL}/${id}`,
    data
  );
  return response.data.data;
};

/**
 * Delete a field mapping
 */
export const deleteFieldMapping = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/${id}`);
};

/**
 * Activate a field mapping (deactivates others for same partner/file type)
 */
export const activateFieldMapping = async (id: string): Promise<FieldMapping> => {
  const response = await axiosInstance.post<ApiResponse<FieldMapping>>(
    `${BASE_URL}/${id}/activate`
  );
  return response.data.data;
};

/**
 * Deactivate a field mapping
 */
export const deactivateFieldMapping = async (
  id: string
): Promise<FieldMapping> => {
  const response = await axiosInstance.post<ApiResponse<FieldMapping>>(
    `${BASE_URL}/${id}/deactivate`
  );
  return response.data.data;
};

/**
 * Get all field mappings (admin)
 */
export const getAllFieldMappings = async (): Promise<FieldMapping[]> => {
  const response = await axiosInstance.get<ApiListResponse<FieldMapping>>(
    BASE_URL
  );
  return response.data.data;
};
